import { useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent, type ReactNode } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  Check,
  GripHorizontal,
  Maximize2,
  MessageCircle,
  Mic,
  MicOff,
  Minimize2,
  Minus,
  Pause,
  Phone,
  PhoneOff,
  Delete,
  Save,
  StickyNote,
  Volume2,
  VolumeX,
  X,
} from "lucide-react";
import { cn } from "@/lib/cn";
import { ROUTES } from "@/constants/routes";
import { queryKeys } from "@/constants/queryKeys";
import { initialsFromName } from "@/lib/media";
import { fetchDirectoryContacts, createNote, fetchNotesByNumber } from "@/services/api";
import { unlockRingtoneAudio, usePhoneStore } from "@/features/phone";
import { useUiStore } from "@/store";
import { useMessagesStore } from "@/features/messages/store/useMessagesStore";
import {
  normalizePhoneDigits,
  openOrCreateDmForContact,
} from "@/features/messages/lib/openOrCreateDm";
import { toast } from "@/components/ui";
import { queryClient } from "@/lib/queryClient";

const KEYPAD: Array<{ key: string; letters?: string }> = [
  { key: "1" },
  { key: "2", letters: "ABC" },
  { key: "3", letters: "DEF" },
  { key: "4", letters: "GHI" },
  { key: "5", letters: "JKL" },
  { key: "6", letters: "MNO" },
  { key: "7", letters: "PQRS" },
  { key: "8", letters: "TUV" },
  { key: "9", letters: "WXYZ" },
  { key: "*" },
  { key: "0", letters: "+" },
  { key: "#" },
];

const NOTES_STORAGE_KEY = "papaya-call-notes";

type SavedNotes = Record<string, { note: string; updatedAt: string }>;

function readLocalNotes(): SavedNotes {
  try {
    const raw = localStorage.getItem(NOTES_STORAGE_KEY);
    if (!raw) return {};
    return JSON.parse(raw) as SavedNotes;
  } catch {
    return {};
  }
}

function writeLocalNote(phoneKey: string, note: string) {
  const next = readLocalNotes();
  if (!note.trim()) {
    delete next[phoneKey];
  } else {
    next[phoneKey] = { note: note.trim(), updatedAt: new Date().toISOString() };
  }
  localStorage.setItem(NOTES_STORAGE_KEY, JSON.stringify(next));
}

function noteKey(value: string) {
  return value.replace(/\D/g, "") || value.trim() || "unknown";
}

function formatDisplay(value: string) {
  const digits = value.replace(/[^\d+*#]/g, "");
  if (digits.startsWith("+") || /[*#]/.test(digits)) return digits;
  if (digits.length <= 3) return digits;
  if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
  if (digits.length <= 10) {
    return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
  return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 10)} ${digits.slice(10)}`;
}

function findContactNameForNumber(
  value: string,
  directory: Array<{ name: string; phone?: string | null }> = [],
): string | null {
  const digits = normalizePhoneDigits(value);
  if (!digits) return null;

  const directoryMatch = directory.find(
    (item) => normalizePhoneDigits(item.phone || "") === digits,
  );
  if (directoryMatch?.name) return directoryMatch.name;

  return null;
}

async function openOrCreateChatForNumber(value: string): Promise<string | null> {
  return openOrCreateDmForContact({ phone: value });
}

const DIAL_KEYS = new Set(["0", "1", "2", "3", "4", "5", "6", "7", "8", "9", "*", "#", "+"]);

function callStatusLabel(
  callStatus: string,
  onHold: boolean,
  timer: string,
  regStatus: string,
) {
  if (callStatus === "incoming") return "Incoming call…";
  if (callStatus === "calling") return "Calling…";
  if (callStatus === "ringing") return "Ringing…";
  if (callStatus === "connected") {
    return onHold ? `On hold · ${timer}` : timer;
  }
  if (regStatus === "online") return "Enter number to call";
  if (regStatus === "connecting") return "Connecting to Asterisk…";
  return "Phone offline";
}

export function DialerPopup() {
  const navigate = useNavigate();
  const open = useUiStore((s) => s.dialerOpen);
  const minimized = useUiStore((s) => s.dialerMinimized);
  const position = useUiStore((s) => s.dialerPosition);
  const closeDialer = useUiStore((s) => s.closeDialer);
  const setDialerMinimized = useUiStore((s) => s.setDialerMinimized);
  const setDialerPosition = useUiStore((s) => s.setDialerPosition);
  const setActiveConversationId = useMessagesStore((s) => s.setActiveConversationId);
  const setSearch = useMessagesStore((s) => s.setSearch);

  const dialNumber = usePhoneStore((s) => s.dialNumber);
  const setDialNumber = usePhoneStore((s) => s.setDialNumber);
  const callStatus = usePhoneStore((s) => s.callStatus);
  const regStatus = usePhoneStore((s) => s.regStatus);
  const remoteNumber = usePhoneStore((s) => s.remoteNumber);
  const muted = usePhoneStore((s) => s.muted);
  const onHold = usePhoneStore((s) => s.onHold);
  const speakerOn = usePhoneStore((s) => s.speakerOn);
  const elapsed = usePhoneStore((s) => s.elapsed);
  const makeCall = usePhoneStore((s) => s.makeCall);
  const hangup = usePhoneStore((s) => s.hangup);
  const accept = usePhoneStore((s) => s.accept);
  const reject = usePhoneStore((s) => s.reject);
  const toggleMute = usePhoneStore((s) => s.toggleMute);
  const toggleHold = usePhoneStore((s) => s.toggleHold);
  const toggleSpeaker = usePhoneStore((s) => s.toggleSpeaker);
  const sendDtmfDigit = usePhoneStore((s) => s.sendDtmf);

  const [showKeypad, setShowKeypad] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [note, setNote] = useState("");
  const [noteSaved, setNoteSaved] = useState(false);
  const [noteSaving, setNoteSaving] = useState(false);
  const numberInputRef = useRef<HTMLInputElement>(null);
  const prevCallStatusRef = useRef(callStatus);
  const dragRef = useRef<{
    startX: number;
    startY: number;
    originX: number;
    originY: number;
  } | null>(null);

  const directoryContactsQuery = useQuery({
    queryKey: queryKeys.directoryContacts("all", ""),
    queryFn: () => fetchDirectoryContacts(),
    enabled: open,
    staleTime: 60_000,
  });

  const inCall = callStatus !== "idle";
  const number = remoteNumber || dialNumber;
  const contactName = useMemo(
    () => findContactNameForNumber(number, directoryContactsQuery.data ?? []),
    [number, directoryContactsQuery.data],
  );

  useEffect(() => {
    if (callStatus === "incoming" && !open) {
      useUiStore.getState().openDialer();
    }
  }, [callStatus, open]);

  useEffect(() => {
    if (!open) return;
    unlockRingtoneAudio();
  }, [open]);

  useEffect(() => {
    if (!inCall) setShowKeypad(false);
  }, [inCall]);

  // After a call ends, clear the note field so the next dial starts clean.
  useEffect(() => {
    const prev = prevCallStatusRef.current;
    prevCallStatusRef.current = callStatus;
    if (prev !== "idle" && callStatus === "idle") {
      setNote("");
      setNoteSaved(false);
      setNoteSaving(false);
      setShowKeypad(false);
    }
  }, [callStatus]);

  useEffect(() => {
    if (!number.trim()) {
      setNote("");
      setNoteSaved(false);
      return;
    }

    const key = noteKey(number);
    const local = readLocalNotes()[key];
    setNote(local?.note ?? "");
    setNoteSaved(false);

    let cancelled = false;
    void fetchNotesByNumber(number)
      .then((rows) => {
        if (cancelled) return;
        const latest = rows[0];
        if (latest?.content && !local?.note) {
          setNote(latest.content);
          writeLocalNote(key, latest.content);
        } else if (latest?.content && local?.note !== latest.content) {
          // Prefer server note when local is only a stale cache
          setNote((current) => (current === (local?.note ?? "") ? latest.content : current));
        }
      })
      .catch(() => {
        /* keep local fallback */
      });
    return () => {
      cancelled = true;
    };
  }, [number]);

  useEffect(() => {
    if (!open || minimized || inCall) return;
    const frame = window.requestAnimationFrame(() => {
      numberInputRef.current?.focus();
    });
    return () => window.cancelAnimationFrame(frame);
  }, [open, minimized, inCall]);

  useEffect(() => {
    if (!open) return;

    const onKeyDown = (event: KeyboardEvent) => {
      const target = event.target as HTMLElement | null;
      const tag = target?.tagName?.toLowerCase();
      const onNumberInput = target === numberInputRef.current;
      const isEditable =
        tag === "textarea" ||
        (tag === "input" && !onNumberInput) ||
        Boolean(target?.isContentEditable);

      if (isEditable) return;
      if (event.ctrlKey || event.metaKey || event.altKey) return;

      if (event.key === "Escape") {
        if (minimized || inCall) return;
        closeDialer();
        return;
      }

      if (event.key === "Enter") {
        if (inCall || !dialNumber.trim()) return;
        event.preventDefault();
        setShowKeypad(false);
        makeCall(dialNumber);
        return;
      }

      if (event.key === "Backspace") {
        if (onNumberInput) return;
        if (inCall && !showKeypad) return;
        event.preventDefault();
        setDialNumber(dialNumber.slice(0, -1));
        return;
      }

      // Number input handles its own typing.
      if (onNumberInput) return;

      let digit = event.key;
      if (event.code === "NumpadMultiply") digit = "*";
      if (event.code === "NumpadAdd" || (event.shiftKey && event.key === "=")) digit = "+";
      if (event.shiftKey && event.key === "3") digit = "#";

      if (!DIAL_KEYS.has(digit)) return;
      if (inCall && !showKeypad) return;

      event.preventDefault();
      if (inCall && showKeypad) {
        sendDtmfDigit(digit);
        setDialNumber(`${dialNumber}${digit}`.slice(0, 32));
        return;
      }
      setDialNumber(`${dialNumber}${digit}`.slice(0, 18));
    };

    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    open,
    minimized,
    inCall,
    showKeypad,
    dialNumber,
    closeDialer,
    setDialNumber,
    makeCall,
    sendDtmfDigit,
  ]);

  useEffect(() => {
    const onPointerMove = (event: PointerEvent) => {
      if (!dragRef.current) return;
      const width = expanded ? 380 : 300;
      const height = expanded ? 680 : 560;
      const nextX = dragRef.current.originX + (event.clientX - dragRef.current.startX);
      const nextY = dragRef.current.originY + (event.clientY - dragRef.current.startY);
      const maxX = window.innerWidth - width;
      const maxY = window.innerHeight - height;
      setDialerPosition({
        x: Math.min(Math.max(12, nextX), Math.max(12, maxX)),
        y: Math.min(Math.max(12, nextY), Math.max(12, maxY)),
      });
    };

    const onPointerUp = () => {
      dragRef.current = null;
    };

    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    return () => {
      window.removeEventListener("pointermove", onPointerMove);
      window.removeEventListener("pointerup", onPointerUp);
    };
  }, [expanded, setDialerPosition]);

  if (!open) return null;

  const startDrag = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as HTMLElement).closest("button, input, textarea")) return;
    dragRef.current = {
      startX: event.clientX,
      startY: event.clientY,
      originX: position.x,
      originY: position.y,
    };
  };

  const press = (key: string) => {
    if (inCall && showKeypad) {
      sendDtmfDigit(key);
      setDialNumber(`${dialNumber}${key}`.slice(0, 32));
      return;
    }
    if (inCall && !showKeypad) return;
    setDialNumber(`${dialNumber}${key}`.slice(0, 18));
  };

  const hangUp = () => {
    hangup();
    setShowKeypad(false);
    setNote("");
    setNoteSaved(false);
    setNoteSaving(false);
  };

  const startCall = () => {
    if (!dialNumber.trim()) return;
    setShowKeypad(false);
    makeCall(dialNumber);
  };

  const saveNote = async () => {
    const content = note.trim();
    if (!content || !number.trim() || noteSaving) return;
    const key = noteKey(number);
    setNoteSaving(true);
    try {
      await createNote({ content, number });
      writeLocalNote(key, content);
      setNoteSaved(true);
      void queryClient.invalidateQueries({ queryKey: queryKeys.notesByNumber(key) });
      toast.success("Note saved");
      window.setTimeout(() => setNoteSaved(false), 1600);
    } catch (err) {
      writeLocalNote(key, content);
      setNoteSaved(true);
      toast.error("Saved offline", "Could not reach notes API; kept locally.");
      window.setTimeout(() => setNoteSaved(false), 1600);
      console.warn("Dialer note save failed", err);
    } finally {
      setNoteSaving(false);
    }
  };

  const goToChat = () => {
    void (async () => {
      const conversationId = await openOrCreateChatForNumber(number);
      if (conversationId) {
        setActiveConversationId(conversationId);
        setSearch("");
        void queryClient.invalidateQueries({ queryKey: ["chat-conversations"] });
      } else {
        setSearch(number.replace(/\D/g, "") || number);
        setActiveConversationId(null);
      }
      navigate(ROUTES.messages);
      setDialerMinimized(true);
    })();
  };

  const timer = `${Math.floor(elapsed / 60)}:${String(elapsed % 60).padStart(2, "0")}`;
  const displayNumber = formatDisplay(number) || "—";
  const avatarLabel = contactName ? initialsFromName(contactName) : null;
  const panelWidth = expanded ? "w-[380px]" : "w-[300px]";
  const statusText = callStatusLabel(callStatus, onHold, timer, regStatus);

  if (minimized) {
    return (
      <div className="fixed right-4 bottom-4 z-[80] w-[280px]">
        <div className="overflow-hidden rounded-[12px] border border-border bg-surface shadow-[0_16px_40px_rgba(42,26,18,0.18)] dark:shadow-[0_16px_40px_rgba(0,0,0,0.5)]">
          <div className="px-3 py-3">
            <div className="flex items-center gap-3">
              <span
                className={cn(
                  "relative inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-surface-muted text-sm font-semibold text-fg",
                  inCall && "ring-2 ring-papaya-leaf/50",
                )}
              >
                {inCall ? (
                  <span className="absolute inset-0 animate-ping rounded-full bg-papaya-leaf/20" />
                ) : null}
                {inCall ? (
                  avatarLabel ? (
                    <span className="relative">{avatarLabel}</span>
                  ) : (
                    <Phone className="relative h-4 w-4 text-fg-muted" />
                  )
                ) : (
                  <Phone className="relative h-4 w-4 text-fg-muted" />
                )}
              </span>
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-fg">
                  {inCall ? contactName || displayNumber : "Assist Phone"}
                </p>
                <p className="truncate text-[11px] text-fg-muted">
                  {inCall ? statusText : dialNumber || statusText}
                </p>
              </div>
              <button
                type="button"
                aria-label="Expand dialer"
                className="rounded-[12px] p-1.5 text-fg-muted hover:bg-surface-muted hover:text-fg"
                onClick={() => setDialerMinimized(false)}
              >
                <Maximize2 className="h-4 w-4" />
              </button>
              <button
                type="button"
                aria-label="Close dialer"
                className="rounded-[12px] p-1.5 text-fg-muted hover:bg-surface-muted hover:text-fg"
                onClick={() => {
                  if (inCall) hangUp();
                  closeDialer();
                }}
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {callStatus === "incoming" ? (
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  aria-label="Reject"
                  onClick={reject}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full bg-danger px-3 text-xs font-semibold text-white hover:bg-danger/90"
                >
                  <PhoneOff className="h-3.5 w-3.5" />
                  Decline
                </button>
                <button
                  type="button"
                  aria-label="Accept"
                  onClick={accept}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full bg-papaya-leaf px-3 text-xs font-semibold text-white hover:bg-papaya-leaf/90"
                >
                  <Phone className="h-3.5 w-3.5" />
                  Answer
                </button>
              </div>
            ) : inCall ? (
              <div className="mt-3 flex items-center justify-end gap-2">
                <button
                  type="button"
                  aria-label={muted ? "Unmute" : "Mute"}
                  onClick={toggleMute}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-fg-muted hover:bg-border"
                >
                  {muted ? <MicOff className="h-3.5 w-3.5" /> : <Mic className="h-3.5 w-3.5" />}
                </button>
                <button
                  type="button"
                  aria-label="Go to chat"
                  onClick={goToChat}
                  className="inline-flex h-8 w-8 items-center justify-center rounded-full bg-surface-muted text-fg-muted hover:bg-border"
                >
                  <MessageCircle className="h-3.5 w-3.5" />
                </button>
                <button
                  type="button"
                  aria-label="End call"
                  onClick={hangUp}
                  className="inline-flex h-8 items-center gap-1.5 rounded-full bg-danger px-3 text-xs font-semibold text-white hover:bg-danger/90"
                >
                  <PhoneOff className="h-3.5 w-3.5" />
                  End
                </button>
              </div>
            ) : null}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "fixed z-[80] flex max-h-[calc(100vh-24px)] flex-col overflow-hidden rounded-[12px] border border-border bg-surface shadow-[0_24px_60px_rgba(42,26,18,0.2)] transition-[width] duration-200 dark:shadow-[0_24px_60px_rgba(0,0,0,0.55)]",
        panelWidth,
      )}
      style={{ left: position.x, top: position.y }}
    >
      <div
        className="cursor-grab select-none border-b border-border bg-surface px-3 pt-3 pb-2.5 active:cursor-grabbing"
        onPointerDown={startDrag}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2 text-fg-muted">
            <GripHorizontal className="h-4 w-4" />
            <span className="text-xs font-semibold tracking-[0.12em] text-fg uppercase">
              Assist Phone
            </span>
            <span
              className={cn(
                "h-1.5 w-1.5 rounded-full",
                regStatus === "online" && "bg-emerald-500",
                regStatus === "connecting" && "animate-pulse bg-amber-400",
                regStatus === "offline" && "bg-zinc-400",
              )}
              title={
                regStatus === "online"
                  ? "Asterisk connected"
                  : regStatus === "connecting"
                    ? "Connecting…"
                    : "Disconnected"
              }
            />
          </div>
          <div className="flex items-center gap-0.5">
            <button
              type="button"
              aria-label={expanded ? "Shrink dialer" : "Enlarge dialer"}
              className="rounded-[12px] p-1.5 text-fg-muted hover:bg-surface-muted hover:text-fg"
              onClick={() => setExpanded((value) => !value)}
            >
              {expanded ? <Minimize2 className="h-4 w-4" /> : <Maximize2 className="h-4 w-4" />}
            </button>
            <button
              type="button"
              aria-label="Minimize dialer"
              className="rounded-[12px] p-1.5 text-fg-muted hover:bg-surface-muted hover:text-fg"
              onClick={() => setDialerMinimized(true)}
            >
              <Minus className="h-4 w-4" />
            </button>
            <button
              type="button"
              aria-label="Close dialer"
              className="rounded-[12px] p-1.5 text-fg-muted hover:bg-surface-muted hover:text-fg"
              onClick={() => {
                if (inCall) hangUp();
                closeDialer();
              }}
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>

      <div className={cn("bg-surface-muted px-4 text-center", expanded ? "py-6" : "py-5")}>
        <div
          className={cn(
            "mx-auto mb-3 flex items-center justify-center rounded-full bg-surface font-semibold text-fg shadow-[0_2px_8px_rgba(42,26,18,0.08)] ring-1 ring-border",
            expanded ? "h-20 w-20 text-xl" : "h-16 w-16 text-lg",
          )}
        >
          {avatarLabel ? avatarLabel : <Phone className="h-6 w-6 text-fg-subtle" />}
        </div>
        {inCall ? (
          <>
            {contactName ? (
              <p className="mb-0.5 text-sm font-semibold text-fg">{contactName}</p>
            ) : null}
            <p className="min-h-[28px] text-xl font-semibold tracking-wide break-all text-fg">
              {displayNumber}
            </p>
          </>
        ) : (
          <>
            {contactName ? (
              <p className="mb-0.5 text-sm font-semibold text-fg">{contactName}</p>
            ) : null}
            <input
              ref={numberInputRef}
              type="tel"
              inputMode="tel"
              autoComplete="off"
              spellCheck={false}
              value={dialNumber}
              onChange={(event) => {
                const next = event.target.value.replace(/[^\d+*#]/g, "").slice(0, 18);
                setDialNumber(next);
              }}
              placeholder="Enter number"
              aria-label="Phone number"
              className="min-h-[28px] w-full bg-transparent text-center text-xl font-semibold tracking-wide text-fg outline-none placeholder:text-fg-subtle"
            />
          </>
        )}
        <p className="mt-1 text-xs font-medium text-fg-muted">
          {inCall ? (
            <span className="inline-flex items-center gap-1.5">
              <span
                className={cn(
                  "h-1.5 w-1.5 rounded-full",
                  onHold ? "bg-papaya" : "animate-pulse bg-papaya-leaf",
                )}
              />
              {statusText}
            </span>
          ) : (
            statusText
          )}
        </p>
      </div>

      <div className="min-h-0 flex-1 space-y-3 overflow-y-auto bg-surface p-3 pb-4">
        {callStatus === "incoming" ? (
          <div className="flex items-center justify-center gap-4 pt-4">
            <button
              type="button"
              aria-label="Decline call"
              onClick={reject}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger text-white shadow-[0_8px_20px_rgba(233,70,77,0.35)] hover:bg-danger/90"
            >
              <PhoneOff className="h-5 w-5" />
            </button>
            <button
              type="button"
              aria-label="Answer call"
              onClick={accept}
              className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-papaya-leaf text-white shadow-[0_8px_20px_rgba(61,140,90,0.35)] hover:bg-papaya-leaf/90"
            >
              <Phone className="h-5 w-5" />
            </button>
          </div>
        ) : !inCall || showKeypad ? (
          <>
            <div className="grid grid-cols-3 justify-items-center gap-y-2.5 px-1">
              {KEYPAD.map((item) => (
                <button
                  key={item.key}
                  type="button"
                  onClick={() => press(item.key)}
                  className={cn(
                    "flex flex-col items-center justify-center rounded-full bg-surface-muted shadow-[0_1px_3px_rgba(42,26,18,0.08)] transition-transform hover:bg-border/40 active:scale-[0.96] dark:shadow-none",
                    expanded ? "h-[58px] w-[58px]" : "h-[52px] w-[52px]",
                  )}
                >
                  <span className="text-[20px] leading-none font-semibold text-fg">
                    {item.key}
                  </span>
                  {item.letters ? (
                    <span className="mt-0.5 text-[8px] font-semibold tracking-[0.14em] text-fg-subtle">
                      {item.letters}
                    </span>
                  ) : (
                    <span className="mt-0.5 h-[10px]" />
                  )}
                </button>
              ))}
            </div>

            {!inCall ? (
              <div className="flex items-center justify-between gap-2 px-2 pt-1">
                <button
                  type="button"
                  aria-label="Backspace"
                  onClick={() => setDialNumber(dialNumber.slice(0, -1))}
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted"
                >
                  <Delete className="h-4 w-4" />
                </button>

                <button
                  type="button"
                  aria-label="Call"
                  onClick={startCall}
                  disabled={!dialNumber.trim() || regStatus !== "online"}
                  className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-[#5b7a8c] text-white shadow-[0_6px_16px_rgba(91,122,140,0.35)] transition-transform hover:scale-[1.03] hover:bg-[#4f6b7b] disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Phone className="h-5 w-5" />
                </button>

                <button
                  type="button"
                  aria-label={speakerOn ? "Speaker off" : "Speaker on"}
                  onClick={toggleSpeaker}
                  className={cn(
                    "inline-flex h-11 w-11 items-center justify-center rounded-full text-fg-muted hover:bg-surface-muted",
                    speakerOn && "bg-papaya/15 text-papaya-deep",
                  )}
                >
                  {speakerOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
                </button>
              </div>
            ) : (
              <div className="flex justify-center pt-1">
                <button
                  type="button"
                  onClick={() => setShowKeypad(false)}
                  className="rounded-full bg-surface-muted px-4 py-2 text-xs font-semibold text-fg-muted hover:text-fg"
                >
                  Hide keypad
                </button>
              </div>
            )}
          </>
        ) : (
          <div className="space-y-3">
            <div className="grid grid-cols-4 gap-2 px-0.5">
              <CallAction
                label={muted ? "Unmute" : "Mute"}
                active={muted}
                onClick={toggleMute}
                icon={muted ? <MicOff className="h-4 w-4" /> : <Mic className="h-4 w-4" />}
              />
              <CallAction
                label="Speaker"
                active={speakerOn}
                onClick={toggleSpeaker}
                icon={speakerOn ? <Volume2 className="h-4 w-4" /> : <VolumeX className="h-4 w-4" />}
              />
              <CallAction
                label={onHold ? "Resume" : "Hold"}
                active={onHold}
                onClick={toggleHold}
                icon={<Pause className="h-4 w-4" />}
              />
              <CallAction
                label="Keypad"
                active={showKeypad}
                onClick={() => setShowKeypad(true)}
                icon={<span className="text-xs font-bold">123</span>}
              />
            </div>

            <button
              type="button"
              onClick={goToChat}
              className="flex w-full items-center justify-center gap-2 rounded-[12px] border border-border bg-surface-muted px-3 py-2.5 text-sm font-semibold text-fg transition-colors hover:bg-border/40"
            >
              <MessageCircle className="h-4 w-4 text-papaya-deep" />
              Go to chat
            </button>

            <div className="rounded-[12px] border border-border bg-surface-muted p-2.5">
              <div className="mb-1.5 flex items-center justify-between gap-2">
                <label className="flex items-center gap-1.5 text-[11px] font-semibold tracking-wide text-fg-muted uppercase">
                  <StickyNote className="h-3 w-3" />
                  Call note
                </label>
                <button
                  type="button"
                  onClick={() => void saveNote()}
                  disabled={!note.trim() || noteSaving}
                  className={cn(
                    "inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold transition-colors",
                    noteSaved
                      ? "bg-papaya-leaf/15 text-papaya-leaf"
                      : "bg-papaya-deep text-white hover:bg-papaya-deep/90 disabled:cursor-not-allowed disabled:opacity-40",
                  )}
                >
                  {noteSaved ? (
                    <>
                      <Check className="h-3 w-3" />
                      Saved
                    </>
                  ) : (
                    <>
                      <Save className="h-3 w-3" />
                      {noteSaving ? "Saving…" : "Save"}
                    </>
                  )}
                </button>
              </div>
              <textarea
                value={note}
                onChange={(event) => {
                  setNote(event.target.value);
                  setNoteSaved(false);
                }}
                placeholder="Add a note for this call…"
                rows={expanded ? 5 : 3}
                className="w-full resize-none rounded-[10px] border-0 bg-surface px-2.5 py-2 text-sm text-fg outline-none placeholder:text-fg-subtle focus:ring-1 focus:ring-papaya/40"
              />
              <p className="mt-1.5 text-[10px] text-fg-subtle">
                Saved to your account for this number
              </p>
            </div>

            <div className="flex justify-center pt-0.5">
              <button
                type="button"
                aria-label="End call"
                onClick={hangUp}
                className="inline-flex h-14 w-14 items-center justify-center rounded-full bg-danger text-white shadow-[0_8px_20px_rgba(233,70,77,0.35)] hover:bg-danger/90"
              >
                <PhoneOff className="h-5 w-5" />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function CallAction({
  label,
  icon,
  onClick,
  active = false,
}: {
  label: string;
  icon: ReactNode;
  onClick: () => void;
  active?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex flex-col items-center gap-1.5 text-fg-muted hover:text-fg"
    >
      <span
        className={cn(
          "inline-flex h-11 w-11 items-center justify-center rounded-full bg-surface-muted",
          active && "bg-papaya/15 text-papaya-deep ring-1 ring-papaya/30",
        )}
      >
        {icon}
      </span>
      <span className="text-[11px] font-medium">{label}</span>
    </button>
  );
}
